/**
 * AI CONTEXT
 * Purpose: exception dashboard for makeups/tryouts/assistants with room grouping.
 * Features: view filters and quick add modals for makeup/tryout.
 * Inputs: studentsByExceptionType from AdminViewContext.
 * Used by: SuperView/AdminView side panels.
 * Refactor: separate grouped rendering and add-actions into child components.
 */
import { PlusOutlined } from "@ant-design/icons"
import { Button, Popover } from "antd"
import { groupBy, map } from "lodash"
import { AnimatePresence } from "motion/react"
import { useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import EmptyState from "#blocks/EmptyState"
import Flex from "#blocks/Flex"
import List from "#blocks/List"
import RadioGroup from "#blocks/RadioGroup"
import { ROOM_COLORS, ROOM_LABELS, ROOMS } from "#constants/classOptions"
import { EXCEPTION_LIST_VIEWS } from "#constants/exceptionListConstants"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import AddMakeupFormModal from "#forms/AddMakeup/AddMakeupFormModal"
import AddTryoutFormModal from "#forms/AddTryout/AddTryoutFormModal"
import { type ExceptionStudentData } from "#hooks/newHooks/useExceptionListData"
import useIsSuperView from "#views/SuperView/useIsSuperView"

import AttendanceSectionHeader from "./AttendanceView/AttendanceSectionHeader"
import ExceptionListItem from "./ExceptionListItem"

const ExceptionListItemWithBoundary = ({
  item,
}: {
  item: ExceptionStudentData
}) => (
  <ComponentErrorBoundary
    variant="line"
    context={`Exception: ${item.student?.name ?? "Unknown"}`}
  >
    <ExceptionListItem item={item} />
  </ComponentErrorBoundary>
)

const PADDING_RIGHT = 30

// Sort by attendance status
// Exceptions seem easy to spot for now, so just saving this for later
// const EXCEPTIONS_SORT_ORDER = {
//   [ATTENDANCE_STATUSES.ENROLLED]: 0,
//   [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: 0,
//   [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: 0,

//   [ATTENDANCE_STATUSES.PLANNED_ABSENCE]: 1,

//   [ATTENDANCE_STATUSES.PRESENT]: 2,
//   [ATTENDANCE_STATUSES.ABSENT]: 2,
//   [ATTENDANCE_STATUSES.TARDY]: 2,

//   [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: 2,
//   [ATTENDANCE_STATUSES.TRYOUT_ABSENT]: 2,
//   [ATTENDANCE_STATUSES.TRYOUT_TARDY]: 2,

//   [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: 2,
//   [ATTENDANCE_STATUSES.MAKEUP_TARDY]: 2,
//   [ATTENDANCE_STATUSES.MAKEUP_ABSENT]: 2,

//   [ATTENDANCE_STATUSES.NOT_ENROLLED]: 3,
// }

const ExceptionList = () => {
  const [view, setView] = useState(EXCEPTION_LIST_VIEWS.all)

  const isSuperView = useIsSuperView()

  const { studentsByExceptionType } = useAdminViewContext()
  const [showAddTryout, setShowAddTryout] = useState(false)
  const [showAddMakeup, setShowAddMakeup] = useState(false)

  const exceptions = useMemo(() => {
    const data = { ...studentsByExceptionType }
    delete data[EXCEPTION_LIST_VIEWS.all]
    return data
  }, [studentsByExceptionType])

  return (
    // display: contents keeps the wrapper out of layout; it exists only because
    // the attendance board renders the same section headers and student rows,
    // so tests need a scope. Flex does not forward data attributes.
    <span data-testid="exception-list-panel" style={{ display: "contents" }}>
      <Flex
        style={{
          padding: isSuperView ? "12px 0px 12px 14px" : "30px 0px 0px 30px",
          border: "1px solid gray",
          borderRadius: 9,
          position: "relative",
          overflow: "hidden",
          flexGrow: 1,
          maxWidth: isSuperView ? "100%" : "calc(100% - 24px - 320px)", // QuickNotes width and padding
          height: "100%",
        }}
        col
      >
        <Flex
          gap={isSuperView ? 20 : 70}
          style={{
            paddingBottom: 14,
            marginRight: isSuperView ? 10 : PADDING_RIGHT,
            alignItems: "center",
            justifyContent: isSuperView ? "space-between" : "flex-start",
          }}
        >
          {isSuperView ? <h2>Exceptions</h2> : <h1>Exceptions</h1>}
          <RadioGroup
            options={[
              { label: "All", value: EXCEPTION_LIST_VIEWS.all },
              { label: "Makeups", value: EXCEPTION_LIST_VIEWS.makeups },
              { label: "Tryouts", value: EXCEPTION_LIST_VIEWS.tryouts },
              { label: "TAs", value: EXCEPTION_LIST_VIEWS.assistants },
            ]}
            onChange={(e) => setView(e.target.value)}
            value={view}
            style={{
              fontSize: isSuperView ? "10px" : undefined,
            }}
            size={isSuperView ? "small" : undefined}
          />
          <Popover
            content={
              <Flex col gap={6}>
                <Button onClick={() => setShowAddMakeup(true)}>
                  + Add New Makeup
                </Button>
                <Button onClick={() => setShowAddTryout(true)}>
                  + Add New Tryout
                </Button>
              </Flex>
            }
            trigger="click"
            placement="rightBottom"
          >
            <Button size={isSuperView ? "small" : undefined}>
              {isSuperView ? <PlusOutlined /> : "+ Add New"}
            </Button>
          </Popover>
        </Flex>

        <Flex
          col
          style={{
            flexGrow: 1,
            width: "100%",
            height: "100%",
            overflowY: "scroll",
            paddingBottom: 100,
            paddingRight: isSuperView ? 4 : 30,
          }}
        >
          {studentsByExceptionType[view]?.length ? (
            <AnimatePresence mode="popLayout">
              {map(exceptions, (exceptions, type) => {
                if (
                  !exceptions.length ||
                  (type !== view && view !== EXCEPTION_LIST_VIEWS.all)
                )
                  return null

                const groupedExceptionsByRoom = groupBy(exceptions, "room")

                return (
                  <AnimateInFlex
                    col
                    key={`exception-${type}`}
                    animation={ANIMATION_BY_KEY.leftSm}
                    style={{ marginBottom: 14 }}
                    gap={4}
                  >
                    <div style={{ width: "50%" }}>
                      <AttendanceSectionHeader
                        type={
                          type as
                            | "assistants"
                            | "tryouts"
                            | "makeups"
                            | "students"
                            | "plannedAbsences"
                        }
                        count={exceptions.length}
                      />
                    </div>

                    {Object.entries(groupedExceptionsByRoom).map(
                      ([room, exceptionsByRoom]) => {
                        return (
                          <Flex
                            col
                            key={room}
                            style={{
                              borderLeft: `5px solid ${ROOM_COLORS[room as ROOMS]}`,
                              color: "black",
                              padding: 4,
                              borderRadius: 4,
                            }}
                          >
                            <Flex>{ROOM_LABELS[room as ROOMS]}</Flex>
                            <List
                              noScroll
                              items={exceptionsByRoom}
                              component={ExceptionListItemWithBoundary}
                              layout="vertical"
                              gap={0}
                              separator={true}
                            />
                          </Flex>
                        )
                      }
                    )}
                  </AnimateInFlex>
                )
              })}
            </AnimatePresence>
          ) : (
            <EmptyState />
          )}
        </Flex>

        {showAddTryout && (
          <AddTryoutFormModal
            onClose={() => setShowAddTryout(false)}
            tryoutId={undefined}
          />
        )}
        {showAddMakeup && (
          <AddMakeupFormModal
            onClose={() => setShowAddMakeup(false)}
            makeupId={undefined}
          />
        )}
      </Flex>
    </span>
  )
}

export default ExceptionList
